import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { TimberListItem } from '../../types';
import {
    addOrMergeTimberItem,
    type TimberItem,
} from '../../utils/materialsDatabaseService';
import { abbreviateSpeciesName, abbreviateTimberDrynessDisplay, supplierInitialsFromName } from '../../utils/timberAbbreviations';
import {
    findNearestPricedTimber,
    piecePriceFromCostPerBdFt,
} from '../../utils/timberQuickAddEstimate';
import './QuickAddTimberDialog.css';

const ESTIMATE_SUPPLIER = 'Estimate';

export interface QuickAddTimberDialogProps {
    item: TimberListItem;
    timberDatabase: TimberItem[];
    onClose: () => void;
    onSaved?: () => void;
}

const formatCostPerBdFt = (value: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);

const QuickAddTimberDialog: React.FC<QuickAddTimberDialogProps> = ({
    item,
    timberDatabase,
    onClose,
    onSaved,
}) => {
    const [costPerBdFt, setCostPerBdFt] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const nearestMatches = useMemo(
        () =>
            findNearestPricedTimber(timberDatabase, {
                species: item.species,
                grade: item.grade,
                dimensions: item.dimensions,
                moistureContent: item.moistureContent,
            }),
        [timberDatabase, item.species, item.grade, item.dimensions, item.moistureContent],
    );

    const parsedCostPerBdFt = parseFloat(costPerBdFt) || 0;
    const piecePricePreview =
        parsedCostPerBdFt > 0 && item.boardFeet > 0
            ? piecePriceFromCostPerBdFt(item.boardFeet, parsedCostPerBdFt)
            : 0;

    const handleSave = async () => {
        if (parsedCostPerBdFt <= 0) {
            setError('Enter a price per board foot greater than zero.');
            return;
        }
        if (item.boardFeet <= 0) {
            setError('Board feet must be calculated from dimensions before saving.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await addOrMergeTimberItem({
                species: item.species.trim(),
                grade: item.grade.trim(),
                dimensions: item.dimensions.trim(),
                moistureContent: item.moistureContent,
                boardFeet: item.boardFeet,
                costPerBdFt: parsedCostPerBdFt,
                piecePrice: piecePriceFromCostPerBdFt(item.boardFeet, parsedCostPerBdFt),
                supplier: ESTIMATE_SUPPLIER,
            });
            onSaved?.();
            onClose();
        } catch (err) {
            console.error('[QuickAddTimberDialog] save failed', err);
            setError('Could not save to the materials database. Try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="quick-add-timber-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quick-add-timber-title"
            onClick={onClose}
        >
            <div className="quick-add-timber-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="quick-add-timber-header">
                    <h4 id="quick-add-timber-title">Quick Add Estimate Price</h4>
                    <button type="button" className="quick-add-timber-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <p className="quick-add-timber-summary">
                    <strong>{item.name || 'Timber'}</strong>
                    {' · '}
                    {abbreviateSpeciesName(item.species)} {item.grade}
                    {item.moistureContent ? ` · ${abbreviateTimberDrynessDisplay(item.moistureContent)}` : ''}
                    {' · '}
                    {item.dimensions}
                    {' · '}
                    {item.boardFeet.toFixed(1)} bf/pc
                </p>
                <p className="quick-add-timber-hint">
                    Adds this size to the materials database with supplier <strong>{ESTIMATE_SUPPLIER}</strong>.
                    Use nearby sizes below to pick a $/bf.
                </p>

                {nearestMatches.length > 0 ? (
                    <div className="quick-add-timber-nearby">
                        <div className="quick-add-timber-nearby-label">Nearest priced sizes</div>
                        <ul className="quick-add-timber-nearby-list">
                            {nearestMatches.map((match) => {
                                const supplierInitials = supplierInitialsFromName(match.supplier);
                                return (
                                <li key={`${match.dimensions}-${match.supplier || 'unknown'}`}>
                                    <span className="quick-add-timber-nearby-dims">{match.dimensions}</span>
                                    <span className="quick-add-timber-nearby-bf">{match.boardFeet.toFixed(1)} bf</span>
                                    {supplierInitials ? (
                                        <span
                                            className="quick-add-timber-nearby-supplier"
                                            title={match.supplier}
                                        >
                                            {supplierInitials}
                                        </span>
                                    ) : (
                                        <span className="quick-add-timber-nearby-supplier quick-add-timber-nearby-supplier--empty" aria-hidden />
                                    )}
                                    <span className="quick-add-timber-nearby-price">
                                        {formatCostPerBdFt(match.costPerBdFt)}/bf
                                    </span>
                                </li>
                                );
                            })}
                        </ul>
                    </div>
                ) : (
                    <p className="quick-add-timber-empty-nearby">
                        No other priced sizes found for this species, grade, and dryness.
                    </p>
                )}

                <label className="quick-add-timber-field" htmlFor="quick-add-cost-per-bf">
                    Price per board foot ($)
                </label>
                <input
                    id="quick-add-cost-per-bf"
                    type="number"
                    min="0"
                    step="0.01"
                    value={costPerBdFt}
                    onChange={(e) => setCostPerBdFt(e.target.value)}
                    placeholder="e.g. 4.25"
                    autoFocus
                />
                {piecePricePreview > 0 && (
                    <p className="quick-add-timber-preview">
                        Piece price: {formatCostPerBdFt(piecePricePreview)} ({item.boardFeet.toFixed(1)} bf × ${parsedCostPerBdFt.toFixed(2)}/bf)
                    </p>
                )}

                {error && <p className="quick-add-timber-error">{error}</p>}

                <div className="quick-add-timber-actions">
                    <button type="button" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="primary"
                        onClick={handleSave}
                        disabled={saving || parsedCostPerBdFt <= 0}
                    >
                        {saving ? 'Saving…' : 'Add to database'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuickAddTimberDialog;
